import { Component, Element, Event, EventEmitter, Host, h, Method, Prop, State, Watch, Fragment } from '@stencil/core';
import parseJson from '../../utils/parse-json';
import { intl } from '../../utils/i18n';
import shadowRootParentOrDocument, { getElementAttributes, setAttributes } from '../../utils/generic-functions';
import { OptionsData, SSelectItem, SDeselectItem, SCloseItem, SErrorItem, SInputItem } from '../../types/types';

export type FilterFunctionParams = {
  item: OptionsData;
  query: string;
};

export type SLoadmoreItem = {
  selectInputId: string;
};
@Component({
  tag: 's-select',
  styleUrl: 's-select.scss',
  shadow: false,
})
export class SSelect {
  @Element() element: HTMLSSelectElement;

  @Prop() placeholder?: string;

  @Prop({ attribute: 'inputid' }) inputId: string;

  @Prop({ attribute: 'optionslist', mutable: true }) optionsList?: string | OptionsData[];

  @Prop() multiple? = false;

  @Prop({ reflect: true }) required? = false;

  @Prop({ reflect: true, mutable: true }) loading? = false;

  @Prop({ reflect: true, attribute: 'loadingnewpage' }) loadingNewPage? = false;

  @Prop() async? = false;

  @Prop({ attribute: 'readonlyinput', reflect: true }) readOnlyInput? = false;

  @Prop({ attribute: 'nosearch', reflect: true }) noSearch? = false;

  @Prop({ reflect: true }) disabled? = false;

  @Prop({ reflect: true }) readonly? = false;

  @Prop({ attribute: 'selectionoptional' }) selectionOptional? = false;

  @Prop({ attribute: 'showselectioncount' }) showSelectionCount? = false;

  @Prop({ attribute: 'predictivesearch' }) predictiveSearch? = false;

  @Prop({ attribute: 'replacealloptions' }) replaceAllOptions? = false;

  @Prop({ attribute: 'lazyload' }) lazyLoad? = false;

  @Prop({ attribute: 'i18n' }) i18n?: Record<string, string> | string = {};

  @Prop() filterFunction?: (params: FilterFunctionParams) => boolean;

  @State() visuallyFocusedItemValue = '';

  @State() results: OptionsData[] = [];

  @State() selectedResults: OptionsData[] = [];

  @State() visuallyFocusedTagIndex: number | null = null;

  @State() showAllResults = true;

  @State() errorMessage = '';

  @State() query = '';

  @State() i18nJson: Record<string, string> = {};

  @State() childProps = {};

  @Event({
    eventName: 's-deselect',
    bubbles: true,
    composed: true,
  })
  sDeselect: EventEmitter<SDeselectItem>;

  @Event({
    eventName: 's-select',
    bubbles: true,
    composed: true,
  })
  sSelect: EventEmitter<SSelectItem>;

  @Event({
    eventName: 's-close',
    bubbles: true,
    composed: true,
  })
  SClose: EventEmitter<SCloseItem>;

  @Event({
    eventName: 's-open',
    bubbles: true,
    composed: true,
  })
  SOpen: EventEmitter<SCloseItem>;

  @Event({
    eventName: 's-error',
    bubbles: true,
    composed: true,
  })
  SError: EventEmitter<SErrorItem>;

  @Event({
    eventName: 's-input',
    bubbles: true,
    composed: true,
  })
  SInput: EventEmitter<SInputItem>;

  @Event({
    eventName: 's-loadmore',
    bubbles: true,
    composed: true,
  })
  SLoadmore: EventEmitter<SLoadmoreItem>;

  @Method()
  async deselectAll() {
    if (!this.multiple && !this.selectionOptional) {
      console.error('The deselectAll() method can only be called on a multi-select or a single select with the selectionoptional attribute');
      return;
    }
    for (const result of this.selectedResults) {
      // Dispatch s-deselect event
      this.sDeselect.emit({ selectInputId: this.inputId, item: result, isDeselectAll: true });
    }
    if (!this.#isNoSearch) {
      this.#input.value = '';
    }
    if (this.multiple && this.#isOpen) {
      this.#closeResults();
    }
    this.#selectInputContainer.focus();

    this.selectedResults = [];
  }

  @Method()
  async selectAll() {
    if (!this.multiple) {
      console.error('The selectAll() method can only be called on a multi-select');
      return;
    }
    const previousSelection = null;
    for (const option of this.#options) {
      this.#dispatchSelectEvent(option, previousSelection);
    }
    this.selectedResults = [...this.#options];
  }

  @Method()
  async getValue() {
    const { selectedResults } = this;
    if (this.multiple) {
      return selectedResults.map(result => {
        return result.value;
      });
    }

    return selectedResults.length ? selectedResults[0].value : '';
  }

  @Watch('multiple')
  watchMultiple(newValue: boolean) {
    if (newValue && this.#containsDefaultSelectedOption) {
      const { selectedResults } = this;
      const firstSelectedResultRemoved = selectedResults.slice(1);
      this.selectedResults = firstSelectedResultRemoved;
    }
  }

  @Watch('disabled')
  watchDisabled(newValue: boolean) {
    if (newValue && this.#isOpen) {
      this.#closeResults();
    }
  }

  @Watch('optionsList')
  watchOptionsList(newValue: string | OptionsData[]) {
    const errorMessage = "Please provide valid JSON string on select's optionslist attribute";
    const optionsJson = parseJson(newValue, errorMessage, []);
    let preselectedOptions = this.multiple || this.async ? [...this.selectedResults] : [];
    let { visuallyFocusedItemValue } = this;
    let shouldKeepVisuallyFocusedItem = false;
    let asyncShouldKeepItemSelected = false;

    this.#options = optionsJson.map(option => {
      // indicate option is selected either if explicitly assigned as such or if the value is already selected in a multi-select
      return {
        ...option,
        id: `${this.#uniqueId}-${option.value}`,
        tagId: `${this.#uniqueId}-${option.value}-tag`,
      };
    });

    for (const option of this.#options) {
      if (option.selected && !this.multiple) {
        preselectedOptions = [option];
        break;
      }
      const indexInPreselectedOptions = preselectedOptions.findIndex(item => {
        return item.value === option.value;
      });

      if (indexInPreselectedOptions > -1 && this.async) {
        asyncShouldKeepItemSelected = true;
      }

      if (option.value === visuallyFocusedItemValue) {
        shouldKeepVisuallyFocusedItem = true;
      }
      if (option.selected && indexInPreselectedOptions === -1) {
        preselectedOptions.push(option);
      } else if (!option.selected && indexInPreselectedOptions > -1 && !asyncShouldKeepItemSelected) {
        // if option is not selected and is present in array of previously selected items, remove it from the array
        preselectedOptions.splice(indexInPreselectedOptions, 1);
      }
    }

    // if there are any existing selected results that are not present in the new optionsArray, those need to be persisted and added to options

    this.selectedResults.forEach(result => {
      if (!this.replaceAllOptions && !this.#options.some(option => result.value === option.value)) {
        this.#options.push(result);
      }
    });

    // first item is selected by default for single select
    if (!preselectedOptions.length && !this.multiple && !this.async && !this.selectionOptional && this.#hasEnabledOptions) {
      preselectedOptions.push(SSelect.getFirstEnabledOption(this.#options));
      this.#containsDefaultSelectedOption = true;
    }

    if (!shouldKeepVisuallyFocusedItem) {
      visuallyFocusedItemValue = preselectedOptions.length ? preselectedOptions[0].value : '';
    }

    this.selectedResults = [...preselectedOptions];
    this.visuallyFocusedItemValue = visuallyFocusedItemValue;

    // if focus is on input when options are set, results must be shown
    if (this.async && SSelect._activeElement === this.#input) {
      this.#showResults(false);
    }

    const optionChildren = Array.from(this.element.querySelectorAll('option'));
    if (optionChildren.length && newValue) {
      optionChildren.forEach(option => {
        option.remove();
      });
    }
  }

  @Watch('loadingNewPage')
  watchLoadingNewPage() {
    if (this.loadingNewPage) {
      this.#resultsDiv.removeEventListener('scroll', this.#handleDropdownScroll);
    } else {
      const { scrollTop } = this.#resultsDiv;
      this.#showResults(false);
      // scroll resultsDiv to same position as it was in before new options were loaded
      this.#resultsDiv.scrollTop = scrollTop;
      this.#resultsDiv.addEventListener('scroll', this.#handleDropdownScroll);
    }
  }

  @Watch('i18n')
  watchI18n() {
    this.i18nJson = parseJson(this.i18n, "Please provide valid JSON string on select's i18n attribute", {});
  }

  #containsDefaultSelectedOption = false;

  #options: OptionsData[] = [];

  #isOpen = false;

  #uniqueId = Math.floor(Math.random() * 1000000000);
  // variable below relates to the mutation observer commented out in connectedCallback()
  // #observer;

  static convertOptionsToObjects(options: HTMLElement[]) {
    return options.map(option => {
      return {
        label: option.textContent.trim(),
        value: option.getAttribute('value'),
        selected: option.hasAttribute('selected'),
        disabled: option.hasAttribute('disabled'),
      };
    });
  }

  static get DEFAULT_INPUT_SIZE() {
    return 1;
  }

  static get INPUT_SIZE_BUFFER_CHARACTERS() {
    return 3;
  }

  static getFirstEnabledOption(options: OptionsData[]) {
    return options.filter(option => !option.disabled)[0];
  }

  get #hasEnabledOptions() {
    return this.#options.some(option => {
      return !option.disabled;
    });
  }

  get #allTags() {
    return Array.from(this.element.querySelectorAll('s-chip')) as HTMLElement[];
  }

  get #visuallyFocusedLi() {
    return this.element.querySelector('li.visually-focused') as HTMLElement;
  }

  get #lastInputFocusableElement() {
    return this.#inputExtended.querySelector('button:not(.hidden)') || this.#input;
  }

  get #allFooterFocusableElements() {
    if (this.#stickyFooterElement) {
      return this.#stickyFooterElement.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
    }
    return null;
  }

  get #lastFooterFocusableElement() {
    const focusableElements = this.#allFooterFocusableElements || [];
    return focusableElements.length ? focusableElements[focusableElements.length - 1] : null;
  }

  get #isStickyFooterVisible() {
    return !!(this.#stickyFooterElement && !this.#stickyFooterElement.classList.contains('hidden'));
  }

  get #isNoSearch() {
    return this.readOnlyInput || this.noSearch;
  }

  get #stickyFooterElement() {
    return this.element.querySelector('[slot="sticky-footer"]');
  }

  get #customNoMatches() {
    return this.element.querySelector("[slot='no-matches']");
  }

  get #inputExtended() {
    return this.element.querySelector('s-input-extended');
  }

  get #input() {
    return this.#inputExtended?.querySelector('input');
  }

  get #selectInputContainer() {
    return this.element.querySelector("[data-ref='select-input-container']") as HTMLElement;
  }

  get #resultsDiv() {
    return this.element.querySelector("[data-ref='results']") as HTMLElement;
  }

  static get _activeElement() {
    let { activeElement } = document;
    if (activeElement?.shadowRoot) {
      activeElement = activeElement.shadowRoot.activeElement;
    }
    return activeElement;
  }

  #handleDropdownScroll = () => {
    if (this.loadingNewPage) return;
    if (this.#resultsDiv.scrollTop + this.#resultsDiv.clientHeight >= this.#resultsDiv.scrollHeight) {
      this.SLoadmore.emit({ selectInputId: this.inputId });
    }
  };

  #selectAnnouncementText(param) {
    const announcementId = 'select.selectionAnnouncement';

    return intl.formatMessage(
      {
        id: announcementId,
        defaultMessage: this.i18nJson[announcementId] || '{SELECTED_ITEM} selected. Press delete to deselect.',
      },
      { SELECTED_ITEM: this.#allTags[param].innerText },
    );
  }

  get #selectText() {
    const ids = {
      noMatches: 'select.noMatches',
      countSingular: 'select.selectionCountSingular',
      countPlural: 'select.selectionCountPlural',
      dismiss: 'select.ariaLabel.dismiss',
      deselectAll: 'select.ariaLabel.deselectAll',
      errorMessage: 'select.errorMessage',
      arialLabelSelect: 'select.ariaLabel.select',
    };

    return {
      noMatches: intl.formatMessage({
        id: ids.noMatches,
        defaultMessage: this.i18nJson[ids.noMatches] || 'No matches found',
      }),
      countSingular: intl.formatMessage(
        {
          id: ids.countSingular,
          defaultMessage: this.i18nJson[ids.countSingular] || '{SELECTION_NUMBER} selection',
        },
        {
          SELECTION_NUMBER: this.selectedResults.length,
        },
      ),
      countPlural: intl.formatMessage(
        {
          id: ids.countPlural,
          defaultMessage: this.i18nJson[ids.countPlural] || '{SELECTION_NUMBER} selections',
        },
        { SELECTION_NUMBER: this.selectedResults.length },
      ),
      dismiss: intl.formatMessage({ id: ids.dismiss, defaultMessage: this.i18nJson[ids.dismiss] || 'Dismiss' }),
      deselectAll: intl.formatMessage({ id: ids.deselectAll, defaultMessage: this.i18nJson[ids.deselectAll] || 'Deselect all' }),
      errorMessage: intl.formatMessage({ id: ids.errorMessage, defaultMessage: this.i18nJson[ids.errorMessage] || 'Please choose a valid option' }),
      ariaLabelSelect: intl.formatMessage({ id: ids.arialLabelSelect, defaultMessage: this.i18nJson[ids.arialLabelSelect] || 'Select one' }),
    };
  }

  #dispatchSelectEvent(item: OptionsData, previousSelection: OptionsData) {
    this.sSelect.emit({ selectInputId: this.inputId, item, previousSelection });
  }

  // the below function is used by the mutation observer that has been commented out in connectedCallback()

  // #handleChildrenChanged() {
  //   let forceRender = false;

  //   if (!this.#customNoMatches && this.element.querySelector("[slot='no-matches']")) {
  //     forceRender = true;
  //   }

  //   if (!this.#stickyFooterElement && this.element.querySelector("[slot='sticky-footer']")) {
  //     if (this.#lastFooterFocusableElement) {
  //       this.#lastFooterFocusableElement.addEventListener('keydown', this.#handleFooterKeyDown);
  //     }
  //     forceRender = true;
  //   }

  //   if (this.#customNoMatches && this.#stickyFooterElement) {
  //     this.#observer.disconnect();
  //   }

  //   if (forceRender) this.render();
  // }

  #handleFooterKeyDown = async (e: KeyboardEvent) => {
    e.stopPropagation();
    switch (e.code) {
      case 'Tab':
        if (!e.shiftKey) {
          if (await this.#inputExtended.getValue()) {
            this.#selectAndClose();
            break;
          }
          this.#closeResults();
        }
        break;
      default:
        break;
    }
  };

  #handlePaste = (e: ClipboardEvent) => {
    e.preventDefault();

    const paste = e.clipboardData.getData('text');
    if (paste) {
      setTimeout(() => {
        this.#input.focus();
      }, 0);
      this.#showResults();
      this.#input.value = paste;
    }
  };

  #handleKeyDownFirst = async (e: KeyboardEvent) => {
    // Chrome autofill sends keydown, keyup, change events, but without 'key'.
    // ShowResults on an autofill.
    if (!e.key) {
      this.#showResults();
      setTimeout(() => {
        this.#input.focus();
      }, 0);
      this.#input.value = e.key;
      return;
    }
    this.#handleKeydown(e);
  };

  #handleKeydown = async (e: KeyboardEvent) => {
    // show results when the user types a character into the focused select-input-container
    if (
      e.key?.length === 1 &&
      e.key !== ' ' &&
      !e.metaKey &&
      !e.ctrlKey && // ignore cmd/alt key combinations (paste)
      SSelect._activeElement === this.#selectInputContainer &&
      !this.#isNoSearch
    ) {
      this.#showResults();
      setTimeout(() => {
        this.#input.focus();
      }, 0);
      this.#input.value = e.key;
      return;
    }
    let value;
    switch (e.key) {
      case 'Tab':
        if (this.#isOpen && !this.#lastFooterFocusableElement) {
          if (
            this.#isNoSearch ||
            (!e.shiftKey && SSelect._activeElement === this.#lastInputFocusableElement) ||
            (e.shiftKey && (SSelect._activeElement === this.#input || SSelect._activeElement === this.#selectInputContainer))
          ) {
            this.#selectAndClose();
            break;
          }
          break;
        } else if (this.#isOpen && e.shiftKey && (SSelect._activeElement === this.#input || SSelect._activeElement === this.#selectInputContainer)) {
          this.#selectAndClose();
          break;
        }
        break;
      case 'ArrowUp':
      case 'Up': // legacy
        e.preventDefault();
        if (this.results.length) {
          this.#visuallyFocusListItem(-1);
          this.#setScrollTop();
        }
        break;
      case 'ArrowDown':
      case 'Down': // legacy
        e.preventDefault();
        if (this.results.length) {
          this.#visuallyFocusListItem(1);
          this.#setScrollTop();
        }
        break;
      case 'ArrowLeft':
      case 'Left': // legacy
        value = await this.#inputExtended.getValue();
        if (this.multiple && !value && this.visuallyFocusedTagIndex !== null) {
          e.preventDefault();
          this.#visuallyFocusTag(this.visuallyFocusedTagIndex, -1);
          break;
        }
        if (this.multiple && !value && this.visuallyFocusedTagIndex === null) {
          this.#visuallyFocusTag(this.visuallyFocusedTagIndex, -1);
        }
        break;
      case 'ArrowRight':
      case 'Right': // legacy
        value = await this.#inputExtended.getValue();
        if (this.multiple && !value && this.visuallyFocusedTagIndex !== null) {
          e.preventDefault();
          this.#visuallyFocusTag(this.visuallyFocusedTagIndex, 1);
        }
        break;
      case 'Backspace':
        if (this.visuallyFocusedTagIndex !== null) {
          e.preventDefault();
          this.#deselectResult(this.#allTags[this.visuallyFocusedTagIndex]);
          this.visuallyFocusedTagIndex = null;
        }
        break;
      case 'Enter':
        if (SSelect._activeElement === this.#inputExtended?.querySelector('button') || SSelect._activeElement === this.element.querySelector("[data-ref='deselect-all-button']")) {
          break;
        }

        e.preventDefault();
        if (this.#visuallyFocusedLi) {
          this.#visuallyFocusedLi.click();
          break;
        }
        this.#showResults();
        break;
      case 'Spacebar':
      case ' ':
        if (SSelect._activeElement === this.#selectInputContainer) {
          e.preventDefault();
          this.#showResults();
          setTimeout(() => {
            this.#input.focus();
          }, 0);
        }

        break;
      case 'Escape':
        e.preventDefault();
        e.stopPropagation();
        if (this.#isOpen) {
          this.#closeResults();
          this.#selectInputContainer.focus();
        }
        break;
      default:
        break;
    }
  };

  #setScrollTop() {
    if (this.#resultsDiv && this.#visuallyFocusedLi) {
      this.#resultsDiv.scrollTop = this.#visuallyFocusedLi.offsetTop - parseInt(window.getComputedStyle(this.#visuallyFocusedLi).getPropertyValue('padding-top'), 10);
    }
  }

  #removeItemFromSelectedResults(tagId: string) {
    const { selectedResults } = this;

    // Using a for loop instead of findIndex() for IE11 reasons
    let index;
    for (let i = 0; i < selectedResults.length; i++) {
      if (selectedResults[i].tagId === tagId) {
        index = i;
        break;
      }
    }

    // Dispatch s-deselect event
    this.sDeselect.emit({ selectInputId: this.inputId, item: selectedResults[index], isDeselectAll: false });
    selectedResults.splice(index, 1);

    this.selectedResults = [...selectedResults];
  }

  // results items should be visually indicated as the user navigates the results list with the keyboard, but focus must remain on the autocomplete input
  #visuallyFocusListItem(increment: number) {
    const visuallyFocusedItemValue = this.visuallyFocusedItemValue || (this.results && this.results[0].value);
    const listItems = Array.from(this.element.querySelectorAll("li[role='option']"));
    let currentIndex = 0;
    let listItem;

    while (currentIndex < listItems.length && listItems[currentIndex].getAttribute('data-value') !== visuallyFocusedItemValue) {
      currentIndex += 1;
    }

    // if user presses down arrow at the end of the list, wrap around to the beginning (unless lazyload is enabled)
    if (currentIndex + increment > listItems.length - 1) {
      listItem = this.lazyLoad ? listItems[currentIndex] : listItems[0];
      // if user presses up arrow at beginning of the list, wrap around to the end (unless lazyload is enabled)
    } else if (currentIndex + increment < 0) {
      listItem = this.lazyLoad ? listItems[currentIndex] : listItems[listItems.length - 1];
    } else {
      const newIndex = currentIndex + increment;
      listItem = listItems[newIndex];
    }
    this.#selectInputContainer.setAttribute('aria-activedescendant', listItem.getAttribute('id'));
    this.visuallyFocusedItemValue = listItem.getAttribute('data-value');
  }

  #visuallyFocusTag(tagIndex: number, increment: number) {
    const currentTagIndex = tagIndex !== null ? tagIndex + increment : this.#allTags.length - 1;
    if (currentTagIndex >= this.#allTags.length) {
      (this.element.querySelector("[data-ref='tag-announce']") as HTMLElement).innerText = '';
      this.visuallyFocusedTagIndex = null;
      return;
    }
    if (currentTagIndex < 0) {
      return;
    }
    (this.element.querySelector("[data-ref='tag-announce']") as HTMLElement).innerText = this.#selectAnnouncementText(currentTagIndex);

    this.visuallyFocusedTagIndex = currentTagIndex;
  }

  async #showResults(shouldScroll = true) {
    if (!this.#options.length && !this.async) return;
    let { visuallyFocusedItemValue } = this;
    // Never filter the results when readonlyinput attribute is present (always show all results)
    const showAllResults = this.#isNoSearch || this.showAllResults;

    document.body.addEventListener('click', this.#handleBodyClick, true);
    this.#isOpen = true;

    const query = (await this.#inputExtended?.getValue())?.trim();
    let filterFunction;
    if (this.filterFunction) {
      filterFunction = item => this.filterFunction({ item, query });
    } else {
      filterFunction = this.predictiveSearch
        ? item => item.label.toLowerCase().trim().startsWith(query.toLowerCase().trim())
        : item => item.label.toLowerCase().trim().includes(query.toLowerCase().trim());
    }

    this.errorMessage = '';
    this.#customNoMatches?.classList?.add('hidden');
    if (!query) {
      this.results = [...this.#options];
      this.showAllResults = false;
      const visuallyFocusedId = `${this.#uniqueId}-${visuallyFocusedItemValue || this.results[0]?.value}`;
      this.#selectInputContainer.setAttribute('aria-activedescendant', visuallyFocusedId);
    } else {
      // Get results
      const results = showAllResults ? this.#options : this.#options.filter(filterFunction);

      if (!this.multiple && !visuallyFocusedItemValue) {
        const visuallyFocusedItem = this.element.querySelector("li[data-selected='true']") || Array.from(this.element.querySelectorAll('li'))[0];
        visuallyFocusedItemValue = visuallyFocusedItem.getAttribute('data-value');
      }

      if (results.length) {
        const isLastClickedItemVisible = results.some(r => r.value === visuallyFocusedItemValue);
        const visuallyFocusedId = isLastClickedItemVisible ? results.find(r => r.value === visuallyFocusedItemValue).id : results[0].id;
        this.#selectInputContainer.setAttribute('aria-activedescendant', visuallyFocusedId);
        this.results = [...results];
        this.showAllResults = false;
        this.visuallyFocusedItemValue = visuallyFocusedItemValue;
      } else {
        this.results = [];
        this.errorMessage = this.#selectText.noMatches;
        this.#customNoMatches?.classList?.remove('hidden');
        this.showAllResults = false;
      }
    }
    if (shouldScroll) {
      this.#setScrollTop();
    }

    if (this.lazyLoad) {
      this.#resultsDiv.addEventListener('scroll', this.#handleDropdownScroll);
    }
  }

  #deselectResult(target: HTMLElement) {
    const innerText = this.visuallyFocusedTagIndex !== null ? this.#allTags[this.visuallyFocusedTagIndex].textContent : target.textContent;
    const deselected = this.selectedResults.filter(r => r.label.toLowerCase().trim() === innerText.toLowerCase().trim())[0];

    this.#removeItemFromSelectedResults(deselected.tagId);
    this.#containsDefaultSelectedOption = false;
  }

  #selectResult(target: HTMLElement) {
    if (target.closest('li').getAttribute('aria-disabled') === 'true') {
      return;
    }

    const { results, selectedResults } = this;
    let previousSelection = null;
    // Update the input and clear results

    // the target might be the span, so choose the closest 'li' and get the 'data-value'
    const selected = results.filter(r => r.value === target.closest('li').getAttribute('data-value'))[0];

    if (this.multiple && selected) {
      if (!selectedResults.find(selectedItem => selectedItem.value === selected.value)) {
        selectedResults.push(selected);
      }
      this.selectedResults = [...selectedResults];
    } else if (selected) {
      this.SClose.emit({ selectInputId: this.inputId });
      this.#selectInputContainer.removeAttribute('aria-activedescendant');

      this.#isOpen = false;
      [previousSelection] = selectedResults;
      this.visuallyFocusedItemValue = selected.value;
      this.results = [];
      this.errorMessage = '';
      this.#customNoMatches?.classList?.add('hidden');
      this.selectedResults = [selected];
      this.showAllResults = true;
    }

    // Dispatch s-select event
    this.#dispatchSelectEvent(selected, previousSelection);

    if (!this.#isNoSearch) {
      this.#containsDefaultSelectedOption = false;
      this.#input.value = '';
    }

    if (this.multiple) {
      this.#showResults(false);
    } else {
      this.#selectInputContainer.focus();
    }
  }

  // Close results as long as its not the user trying to click on a result
  #handleBodyClick = (e: Event) => {
    const inputDiv = this.#selectInputContainer;
    const resultsEl = this.element.querySelector('[data-ref=results]');
    const tags = this.#allTags;
    const stickyFooter = this.#stickyFooterElement;

    if (this.loading) {
      this.loading = false;
    }

    // Note: e.composedPath()[0] is intentionally used here instead of e.target to provide Shadow DOM support.
    // If the s-select is used within a Shadow DOM then the browser redirects e.target to the web component root.
    const target = e.composedPath()[0] as HTMLElement;
    if (resultsEl && !resultsEl.contains(target) && !inputDiv.contains(target)) {
      for (let i = 0; i < tags.length; i++) {
        if (tags[i].contains(target)) {
          return;
        }
      }

      if (stickyFooter && stickyFooter.contains(target)) {
        return;
      }

      if (this.#inputExtended?.contains(target)) {
        return;
      }

      if ((this.multiple || this.selectionOptional) && this.#isOpen) {
        this.#selectInputContainer.focus();
        this.#closeResults();
        return;
      }

      this.#selectAndClose();
    }
  };

  #handleMultiselectLiClick(target: HTMLElement) {
    if (target.closest('li').hasAttribute('disabled')) {
      return;
    }
    if (target.closest('li').getAttribute('data-selected') === 'true') {
      this.#deselectResult(target);
    } else {
      this.#selectResult(target);
    }
    this.visuallyFocusedItemValue = target.closest('li').getAttribute('data-value');
  }

  #handleSelectTriggerClick = (e: Event) => {
    const target = e.composedPath()[0] as HTMLElement;
    if (target.closest('s-chip') || target.closest("[data-ref='deselect-all-button']") || this.disabled || this.readonly) {
      return;
    }
    const { results } = this;

    if (results.length || this.#isOpen) {
      this.#isOpen = false;
      this.#closeResults();
      return;
    }
    this.SOpen.emit({ selectInputId: this.inputId });
    this.#showResults();
    this.#isOpen = true;

    if (!this.#isNoSearch) {
      this.#input.focus();
    }
  };

  #handleSelectTriggerKeydown(e: KeyboardEvent) {
    if (e.key === 'Spacebar' && !this.results.length) {
      e.stopPropagation();
      this.#handleSelectTriggerClick(e);
    }
  }

  #handleDeselectButtonClick = () => {
    this.deselectAll();
    this.#selectInputContainer.focus();
  };

  #handleSClear = () => {
    this.#showResults();
    setTimeout(() => {
      this.#input.focus();
    }, 0);
  };

  #selectAndClose() {
    if (this.results.length) {
      this.#selectResult(this.#visuallyFocusedLi);
    }
    this.#closeResults();
  }

  #closeResults() {
    // emit error event if necessary before resetting state
    if (this.errorMessage) {
      this.SError.emit({ selectInputId: this.inputId, errorMessage: this.#selectText.errorMessage });
    }

    document.body.removeEventListener('click', this.#handleBodyClick, true);
    this.#resultsDiv.addEventListener('scroll', this.#handleDropdownScroll);

    this.#selectInputContainer.removeAttribute('aria-activedescendant');
    if (this.multiple || this.results.length) {
      this.SClose.emit({ selectInputId: this.inputId });
    }
    this.#isOpen = false;
    this.results = [];
    this.errorMessage = '';

    this.#customNoMatches?.classList?.add('hidden');
    if (!this.#isNoSearch) {
      this.#input.value = '';
    }
    this.showAllResults = true;
  }

  #handleInput = async e => {
    e.stopPropagation();

    this.#showResults();
    this.SInput.emit({ inputValue: e.detail.value, selectInputId: this.inputId });
  };

  #handleInputFocus() {
    if (this.disabled) return;
    this.#selectInputContainer.setAttribute('focused', 'true');
  }

  #handleInputBlur() {
    this.#selectInputContainer.removeAttribute('focused');
  }

  #tagsText() {
    return this.#allTags
      .map(tag => {
        return tag.id;
      })
      .join(' ');
  }

  #multiselectSelectionHtml() {
    if (this.showSelectionCount) {
      return this.#selectionCountHtml();
    }
    return this.#tagsHtml();
  }

  #selectionCountHtml() {
    const { selectedResults } = this;
    const defaultStringDescriptor = selectedResults.length === 1 ? this.#selectText.countSingular : this.#selectText.countPlural;

    if (this.multiple && selectedResults.length) {
      return <span data-ref="selection-count-span">{defaultStringDescriptor}</span>;
    }
    return '';
  }

  #tagsHtml() {
    const { selectedResults } = this;
    if (this.multiple)
      return selectedResults.map((result, i) => (
        <s-chip disabled={this.disabled || this.readonly} visually-focused={i === this.visuallyFocusedTagIndex && this.element.contains(SSelect._activeElement)} id={result.tagId}>
          <span data-ref="tag-text">{result.label}</span>
          {!this.disabled && !this.readonly && (
            <button
              type="button"
              data-ref="dismiss-button"
              tabindex="-1"
              onClick={() => this.#removeItemFromSelectedResults(result.tagId)}
              class="ghost icon"
              aria-label={this.#selectText.dismiss}
            >
              <s-icon name="close" aria-hidden="true"></s-icon>
            </button>
          )}
        </s-chip>
      ));
    return '';
  }

  #liHtml(result: OptionsData) {
    const { visuallyFocusedItemValue, selectedResults, results } = this;
    const isLastClickedItemVisible = results.some(r => r.value === visuallyFocusedItemValue);
    const visuallyFocused = visuallyFocusedItemValue && isLastClickedItemVisible ? visuallyFocusedItemValue === result.value : results[0].value === result.value;

    return (
      <li
        tabindex="-1"
        onClick={this.multiple ? (e: Event) => this.#handleMultiselectLiClick(e.composedPath()[0] as HTMLElement) : e => this.#selectResult(e.composedPath()[0] as HTMLElement)}
        data-id={result.id}
        data-value={result.value}
        class={visuallyFocused ? 'visually-focused' : ''}
        id={result.id}
        role="option"
        aria-selected={visuallyFocused ? 'true' : 'false'}
        data-selected={selectedResults.find(selectedItem => selectedItem.value === result.value) ? 'true' : 'false'}
        aria-disabled={result.disabled ? 'true' : 'false'}
      >
        <span>{result.label}</span>
      </li>
    );
  }

  #selectionHtml(inputValue, results, selectedResults) {
    return (
      <Fragment>
        {/* the below span is present purely for axe testing purposes, because a combobox requires a textbox child
        but the nosearch variant doesn't have a textbox or input child element, and only the combobox role is appropriate for the s-select */}
        {this.#isNoSearch && <span role="textbox" aria-hidden="true"></span>}
        {!selectedResults.length ? <span data-ref="placeholder">{this.placeholder}</span> : <span data-ref="select-value">{inputValue}</span>}
        <div data-ref="tag-announce" aria-live="polite" class="visually-hidden"></div>
        {this.#multiselectSelectionHtml()}
        {selectedResults.length && (this.multiple || this.selectionOptional) ? (
          <button
            type="button"
            class="ghost icon"
            data-ref="deselect-all-button"
            onClick={this.#handleDeselectButtonClick}
            aria-label={this.#selectText.deselectAll}
            disabled={this.disabled || this.readonly}
          >
            <s-icon name="close" aria-hidden="true"></s-icon>
          </button>
        ) : (
          ''
        )}
        {this.#options && this.#options.length ? (
          <div data-ref="disclosure-icon">
            <s-icon name={results.length ? 'chevron-up' : 'chevron-down'} aria-hidden="true"></s-icon>
          </div>
        ) : (
          ''
        )}
      </Fragment>
    );
  }

  #ulHtml(results) {
    return (
      <ul
        data-ref="results"
        role="listbox"
        tabindex="0"
        aria-label={shadowRootParentOrDocument(this.element).querySelector(`label[for='${this.inputId}']`)?.textContent || null}
        id={this.#uniqueId.toString()}
        class={this.#isStickyFooterVisible ? 'bottom-border-radius-none' : null}
        aria-multiselectable={this.multiple ? 'true' : 'false'}
      >
        {this.loading && (
          <li data-ref="loading">
            <s-loader loading></s-loader>
          </li>
        )}
        {results.length && !this.loading ? results.map(r => this.#liHtml(r)) : ''}
        {this.loadingNewPage && (
          <li data-ref="loading-new-page">
            <s-loader loading class="small"></s-loader>
          </li>
        )}
        <li data-ref="nomatch" class={this.errorMessage && !this.loading ? '' : 'hidden'}>
          {this.errorMessage && <slot name="no-matches">{this.errorMessage}</slot>}
        </li>
      </ul>
    );
  }

  connectedCallback() {
    this.watchI18n();
    this.watchOptionsList(this.optionsList);
    const optionChildren = Array.from(this.element.querySelectorAll('option'));
    if (optionChildren.length) {
      this.optionsList = SSelect.convertOptionsToObjects(optionChildren);
      optionChildren.forEach(option => {
        option.remove();
      });
    }

    if (this.#stickyFooterElement && this.#lastFooterFocusableElement) {
      this.#lastFooterFocusableElement.addEventListener('keydown', this.#handleFooterKeyDown);
    }

    this.element.addEventListener('paste', this.#handlePaste);

    // It's unclear whether we still need the below mutation observer after the Stencil migration, but it's breaking unit tests
    // should revisit this after we have an Angular playground app using v3 components

    // // some frameworks, such as Angular, do not include children when the component is added to the DOM
    // // the children are appended in a separate operation and can only be detected and managed via a
    // // mutation observer
    // if (!this.#customNoMatches || !this.#stickyFooterElement) {
    //   this.#observer = new MutationObserver(() => this.#handleChildrenChanged());
    //   this.#observer.observe(this.element, { subtree: true, childList: true });
    // }

    if (this.#customNoMatches) {
      this.#customNoMatches.classList.add('hidden');
    }
  }

  componentWillLoad() {
    // PF-3517
    // The react-output-target library does not want to assign an empty class list to a component.
    // This adds a default class so it is never empty.
    this.element.classList.add('default-class');
    this.childProps = getElementAttributes(this.element.attributes);
    // PF-4209: In React, the selectionOptional prop is not applied correctly in connectedCallback on a conditional render of this component
    // we found that running watchOptionsList here solves the issue
    this.watchOptionsList(this.optionsList);
  }

  componentDidLoad() {
    if (this.#input) {
      setAttributes(this.#input, this.childProps);
    }
  }

  disconnectedCallback() {
    window.removeEventListener('paste', this.#handlePaste);
  }

  render() {
    const { results, errorMessage, selectedResults } = this;
    const inputContainerClassList = selectedResults.length && (this.multiple || this.selectionOptional) ? 'contains-clear-button' : '';
    let inputValue = '';
    const parentLabelId = shadowRootParentOrDocument(this.element).querySelector(`label[for='${this.inputId}']`)?.id;
    let ariaLabelledByIds = null;
    if (parentLabelId) {
      ariaLabelledByIds = `${parentLabelId} ${this.multiple ? this.#tagsText() : ''}`.trim();
    } else if (this.element.hasAttribute('aria-labelledby')) {
      ariaLabelledByIds = this.element.getAttribute('aria-labelledby');
    }
    // set inputValue to first selectedResult if it is a single select
    if (selectedResults.length && !this.multiple) {
      inputValue = selectedResults[0].label;
    }
    const searchInputId = `${this.#uniqueId}-search-input`;

    return (
      <Host onKeyDown={this.#handleKeyDownFirst}>
        <div
          aria-expanded={this.#isOpen.toString()}
          onClick={e => this.#handleSelectTriggerClick(e)}
          onKeyDown={e => this.#handleSelectTriggerKeydown(e)}
          class={inputContainerClassList}
          data-ref="select-input-container"
          tabindex={this.disabled || this.readonly ? '-1' : '0'}
          aria-disabled={this.disabled || this.readonly}
          aria-labelledby={ariaLabelledByIds}
          aria-label={ariaLabelledByIds ? null : this.element.getAttribute('aria-label') || this.#selectText.ariaLabelSelect}
          aria-controls={this.#uniqueId.toString()}
          aria-owns={`${this.#uniqueId.toString()} ${!this.#isNoSearch && searchInputId}`}
          role="combobox"
        >
          {this.#selectionHtml(inputValue, results, selectedResults)}
        </div>

        <div data-ref="select-ul-container" class={results.length || this.loading || errorMessage || this.#isOpen ? '' : 'hidden'}>
          {this.#isNoSearch ? (
            ''
          ) : (
            <div data-ref="search-input-div">
              <s-input-extended
                type="search-live"
                data-ref="select-input"
                inputId={this.inputId}
                ons-clear={this.#handleSClear}
                ons-input={this.#handleInput}
                onBlur={this.#handleInputBlur}
                aria-controls={this.#uniqueId.toString()}
                id={`${this.#uniqueId}-search-input`}
                role="textbox"
                aria-autocomplete="list"
                onFocus={this.#handleInputFocus}
                aria-labelledby={shadowRootParentOrDocument(this.element).querySelector(`label[for='${this.inputId}']`)?.id || null}
              ></s-input-extended>
            </div>
          )}

          {this.#ulHtml(results)}

          <slot name="sticky-footer"></slot>
        </div>
      </Host>
    );
  }
}
